import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from './Toast.tsx';
import { LanguageSelect, languageOptions, useEnabledLanguages, type EnabledLanguage } from './LanguageSelect.tsx';

/**
 * Your own account — RX-AUTH-08.
 *
 * Every app had a sign-in and nowhere to go afterwards: no screen anywhere
 * where a person could see or change their own name, language or timezone,
 * and no way at all to add an email address.
 *
 * Adding one is the point. Identity stays the phone number, because the
 * person this app exists for is often 68 with an inbox they check twice a
 * year — but somebody who *can* use email should be able to sign in with it.
 * Mobile is the default; email is a second door, never a replacement.
 *
 * One copy, in the shared package, because four apps need it and four
 * identical copies is how a fix reaches one surface and not the others. The
 * transport is passed in, since each app talks to the API through its own
 * client.
 */
export interface ProfileData {
  id: string;
  name: string | null;
  phoneNumber: string | null;
  phoneVerified: boolean;
  email: string | null;
  emailVerified: boolean;
  pendingEmail: string | null;
  pendingPhone?: string | null;
  locale: string;
  timezone: string;
  presentationMode: string;
  isPlatformAdmin: boolean;
  /** Set when deletion has been requested and the grace period is running. */
  deletionScheduledAt?: number | null;
  deletionDueAt?: number | null;
}

export interface SessionsData {
  sessions: Array<{
    id: string;
    current: boolean;
    ipAddress: string | null;
    userAgent: string | null;
    createdAt: number;
    expiresAt: number;
  }>;
  devices: Array<{
    id: string;
    platform: string;
    model: string | null;
    manufacturer: string | null;
    appVersion: string | null;
    lastHeartbeatAt: number | null;
    notificationsGranted: boolean;
    revoked: boolean;
  }>;
  note: string;
}

/** The three calls this needs, so the component stays app-agnostic. */
export interface ProfileTransport {
  get: <T>(path: string) => Promise<T>;
  put: <T>(path: string, body?: unknown) => Promise<T>;
  post: <T>(path: string, body?: unknown) => Promise<T>;
  del: <T>(path: string) => Promise<T>;
}


/** Enough to cover where DrBell's people actually are. */
const ZONES = [
  'Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London',
  'America/New_York', 'America/Chicago', 'America/Los_Angeles',
  'Australia/Sydney', 'UTC',
];

/**
 * The profile API's refusals, in words.
 *
 * Each app's own client maps the codes it knows about, and none of them knows
 * these — this surface is newer than all three. So a person who mistyped a
 * six-digit code was shown the string `wrong_code`, inside the one dialog
 * where that string is the entire explanation of what just happened. Same
 * shape as the org console's raw `org_suspended`, one component later.
 *
 * Unmapped codes fall through unchanged rather than becoming "something went
 * wrong", so an unknown refusal is still reportable.
 */
const REFUSALS: Record<string, string> = {
  wrong_code: 'That code is not right. Check the digits and try again — after a few wrong tries the code stops working and you will need a new one.',
  expired: 'That code has expired. Codes last ten minutes; ask for a new one.',
  code_expired: 'That code has expired. Codes last ten minutes; ask for a new one.',
  too_many_attempts: 'Too many wrong tries, so that code has been cancelled. Start again with a new one.',
  nothing_pending: 'There is no address waiting to be confirmed. Enter one first.',
  email_taken: 'Another DrBell account already uses that address.',
  reserved_domain: 'That address cannot be used.',
  would_lock_you_out: 'That is the only way you can sign in, so it cannot be removed.',
  not_found: 'Your account could not be loaded. Try signing out and back in.',
};

const message = (e: unknown) => {
  const raw = e instanceof Error ? e.message : String(e);
  return REFUSALS[raw] ?? raw;
};

export function Profile({ api, onClose, onSaved }: {
  api: ProfileTransport;
  onClose: () => void;
  /** Called after a change that the surrounding app may want to react to. */
  onSaved?: () => void;
}) {
  const [me, setMe] = useState<ProfileData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setMe(await api.get<ProfileData>('/api/me/profile'));
    } catch (e) {
      setError(message(e));
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  return (
    <div className="fixed inset-0 z-40 flex items-start justify-center overflow-y-auto bg-black/50 p-4" role="dialog" aria-modal="true" aria-label="Your account">
      <div className="my-8 w-full max-w-lg rounded-2xl border border-(--line) bg-(--bg) p-5">
        <div className="flex items-center gap-3">
          <h2 className="text-lg font-semibold">Your account</h2>
          <button
            onClick={onClose}
            className="ml-auto min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)"
          >
            Close
          </button>
        </div>

        {error && (
          <p role="alert" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
            {error}
          </p>
        )}

        {!me && !error && <p className="mt-6 text-sm text-(--muted)">Loading…</p>}

        {me && (
          <>
            <Details me={me} api={api} onSaved={async () => { await load(); onSaved?.(); }} />
            <ContactSection kind="phone" me={me} api={api} onChanged={async () => { await load(); onSaved?.(); }} />
            <ContactSection kind="email" me={me} api={api} onChanged={async () => { await load(); onSaved?.(); }} />
            <HowYouSignIn me={me} />
            <Sessions api={api} />
            <YourData me={me} api={api} onChanged={async () => { await load(); onSaved?.(); }} />
          </>
        )}
      </div>
    </div>
  );
}

function Details({ me, api, onSaved }: {
  me: ProfileData;
  api: ProfileTransport;
  onSaved: () => void | Promise<void>;
}) {
  const [name, setName] = useState(me.name ?? '');
  const [locale, setLocale] = useState(me.locale);
  const [timezone, setTimezone] = useState(me.timezone);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  const dirty = name !== (me.name ?? '') || locale !== me.locale || timezone !== me.timezone;
  // The languages DrBell offers now (the admin's switch, docs/31), searchable.
  const loadLanguages = useCallback(() => api.get<{ locales: EnabledLanguage[] }>('/api/i18n/locales'), [api]);
  const enabled = useEnabledLanguages(loadLanguages);
  const languages = useMemo(() => languageOptions(enabled, me.locale), [enabled, me.locale]);

  return (
    <section className="mt-5">
      <label className="block">
        <span className="text-xs text-(--muted)">Your name</span>
        <input
          value={name}
          onChange={(e) => { setName(e.target.value); setSaved(false); }}
          placeholder="Not set"
          className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
        />
        <span className="mt-1 block text-xs text-(--muted)">
          What colleagues and family see instead of your phone number.
        </span>
      </label>

      <div className="mt-3 block">
        <span className="text-xs text-(--muted)">Language</span>
        <LanguageSelect
          className="mt-1"
          value={locale}
          options={languages}
          onChange={(code) => { setLocale(code); setSaved(false); }}
        />
        {/* Said here because it is the one thing people expect and must not
            get: translating a medicine name could name something that does
            not exist, or something else entirely. */}
        <span className="mt-1 block text-xs text-(--muted)">
          What DrBell says to you. Medicine names are never translated.
        </span>
      </div>

      <label className="mt-3 block">
        <span className="text-xs text-(--muted)">Your timezone</span>
        <select
          value={timezone}
          onChange={(e) => { setTimezone(e.target.value); setSaved(false); }}
          className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
        >
          {[...new Set([timezone, ...ZONES])].map((z) => (
            <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>
          ))}
        </select>
        <span className="mt-1 block text-xs text-(--muted)">
          {/* What it actually does — B2C audit: this said times are shown in
              this zone, while every screen shows the person's own clock. */}
          Used for your quiet hours: when alerts to you are held back. Doses
          are always shown and rung on the person&rsquo;s own clock — their 8am
          tablet is at their 8am, wherever you are.
        </span>
      </label>

      {error && (
        <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}

      <div className="mt-3 flex items-center gap-3">
        <button
          disabled={!dirty || busy}
          onClick={async () => {
            setBusy(true);
            setError(null);
            try {
              await api.put('/api/me/profile', {
                name: name.trim() || null,
                locale,
                timezone,
              });
              setSaved(true);
              toast.success('Your account was updated');
              await onSaved();
            } catch (e) {
              setError(message(e));
            } finally {
              setBusy(false);
            }
          }}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
        >
          {busy ? 'Saving…' : 'Save'}
        </button>
        {saved && !dirty && <span className="text-sm text-(--muted)">Saved.</span>}
      </div>
    </section>
  );
}


/**
 * The two doors into an account, which behave identically: type it, a code
 * goes to it, type the code back. One component, because two copies of this
 * flow is two places for the lock-out guard to be forgotten.
 */
interface SendResult { delivered?: boolean; reason?: string; email?: string; phone?: string }

const CONTACT = {
  email: {
    title: 'Email address',
    send: (api: ProfileTransport, value: string) =>
      api.post<SendResult>('/api/me/email', { email: value }),
    verify: (api: ProfileTransport, code: string) => api.post('/api/me/email/verify', { code }),
    remove: (api: ProfileTransport) => api.del('/api/me/email'),
    placeholder: 'you@example.com',
    valid: (v: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(v.trim()),
    blurb: 'Add one and you can sign in with a code sent to it. Where SMS is not available yet, this is the way in.',
    removedTitle: 'Email removed',
    removedBody: 'Your mobile number still signs you in.',
    confirmedTitle: 'Email confirmed',
    confirmedBody: 'You can sign in with it as well as with your number.',
    different: 'Use a different address',
  },
  phone: {
    title: 'Mobile number',
    send: (api: ProfileTransport, value: string) =>
      api.post<SendResult>('/api/me/phone', { phone: value }),
    verify: (api: ProfileTransport, code: string) => api.post('/api/me/phone/verify', { code }),
    remove: (api: ProfileTransport) => api.del('/api/me/phone'),
    placeholder: '+919000000000',
    valid: (v: string) => /^\+[1-9]\d{7,14}$/.test(v.trim()),
    blurb: 'Add one and you can sign in with a code sent by SMS. Caregiver invitations travel by number, so this is worth having.',
    removedTitle: 'Number removed',
    removedBody: 'Your email address still signs you in.',
    confirmedTitle: 'Number confirmed',
    confirmedBody: 'You can sign in with it as well as with your email.',
    different: 'Use a different number',
  },
} as const;

function ContactSection({ me, api, onChanged, kind }: {
  me: ProfileData;
  api: ProfileTransport;
  onChanged: () => void | Promise<void>;
  kind: 'email' | 'phone';
}) {
  const it = CONTACT[kind];
  const current = kind === 'email' ? me.email : me.phoneNumber;
  const confirmed = kind === 'email' ? me.emailVerified : me.phoneVerified;
  const pending = kind === 'email' ? me.pendingEmail : me.pendingPhone;
  const [address, setAddress] = useState(pending ?? '');
  const [stage, setStage] = useState<'idle' | 'code'>(pending ? 'code' : 'idle');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const send = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const r = await it.send(api, address.trim());
      setStage('code');
      /*
       * Say what actually happened.
       *
       * There is no email provider configured yet, so in production the code
       * is recorded and sent nowhere. "Check your inbox" would cost somebody
       * ten minutes of refreshing an empty mailbox before they concluded the
       * product was broken. Defect 78 was exactly this, for SMS.
       */
      setNotice(
        r.delivered !== false
          ? `A six-digit code is on its way to ${r.email ?? r.phone ?? address.trim()}.`
          : 'Email sending is not switched on yet, so no code was sent. Ask DrBell to finish setting it up — your mobile number keeps working in the meantime.',
      );
    } catch (e) {
      setError(message(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mt-6 border-t border-(--line) pt-5">
      <h3 className="font-medium">{it.title}</h3>

      {current ? (
        <div className="mt-2 flex flex-wrap items-center gap-3">
          <span className="text-sm">{current}</span>
          {confirmed ? (
            <span className="rounded-full bg-(--accent-soft) px-2 py-0.5 text-xs text-(--accent)">
              confirmed
            </span>
          ) : (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-700">
              not confirmed
            </span>
          )}
          <button
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await it.remove(api);
                toast.success(it.removedTitle, it.removedBody);
                await onChanged();
              } catch (e) {
                setError(message(e));
              } finally {
                setBusy(false);
              }
            }}
            disabled={busy}
            className="ml-auto min-h-9 rounded-lg border border-(--line) px-2 text-xs hover:border-red-500 disabled:opacity-40"
          >
            Remove
          </button>
        </div>
      ) : stage === 'idle' ? (
        <p className="mt-1 text-sm text-(--muted)">{it.blurb}</p>
      ) : null}

      {!current && stage === 'idle' && (
        <div className="mt-3 flex flex-wrap gap-2">
          <input
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && it.valid(address)) void send(); }}
            type={kind === 'email' ? 'email' : 'tel'}
            inputMode={kind === 'email' ? 'email' : 'tel'}
            autoComplete={kind === 'email' ? 'email' : 'tel'}
            placeholder={it.placeholder}
            className="min-h-11 flex-1 rounded-lg border border-(--line) bg-(--panel) px-3"
          />
          <button
            disabled={busy || !it.valid(address)}
            onClick={() => void send()}
            className="min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent) disabled:opacity-40"
          >
            {busy ? 'Sending…' : 'Send a code'}
          </button>
        </div>
      )}

      {!current && stage === 'code' && (
        <div className="mt-3">
          {/*
            The address comes from local state, not from the reloaded profile.
            Reading `me.pendingEmail` meant the code box appeared with nothing
            naming the address it belonged to — because this stage is entered
            from the response, before any refetch. Somebody typing a code has
            to be able to see which address they are confirming, not least to
            notice they typed it wrongly.
          */}
          {address && (
            <p className="text-sm text-(--muted)">
              Confirming <strong className="text-(--fg)">{address}</strong>
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            <input
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              placeholder="000000"
              className="min-h-11 w-32 rounded-lg border border-(--line) bg-(--panel) px-3 text-center tracking-[0.3em]"
            />
            <button
              disabled={busy || code.length !== 6}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await it.verify(api, code);
                  toast.success(it.confirmedTitle, it.confirmedBody);
                  setStage('idle');
                  setCode('');
                  setNotice(null);
                  await onChanged();
                } catch (e) {
                  setError(message(e));
                } finally {
                  setBusy(false);
                }
              }}
              className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white disabled:opacity-40"
            >
              {busy ? 'Checking…' : 'Confirm'}
            </button>
            <button
              onClick={() => { setStage('idle'); setCode(''); setNotice(null); setAddress(''); }}
              className="min-h-11 rounded-lg border border-(--line) px-3 text-sm"
            >
              {it.different}
            </button>
          </div>
        </div>
      )}

      {notice && (
        <p className="mt-3 rounded-lg border border-(--line) bg-(--panel) px-3 py-2 text-sm text-(--muted)">
          {notice}
        </p>
      )}

      {error && (
        <p role="alert" className="mt-3 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-500">
          {error}
        </p>
      )}
    </section>
  );
}
function HowYouSignIn({ me }: { me: ProfileData }) {
  return (
    <section className="mt-6 border-t border-(--line) pt-5">
      <h3 className="font-medium">How you sign in</h3>
      <ul className="mt-2 space-y-2 text-sm">
        <li className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{me.phoneNumber ?? 'No number'}</span>
          <span className="rounded-full bg-(--accent-soft) px-2 py-0.5 text-xs text-(--accent)">
            default
          </span>
          {/* Said plainly, because "can I delete this?" is the next question
              and the honest answer is no. */}
          <span className="text-(--muted)">— always works, and cannot be removed</span>
        </li>
        <li className="text-(--muted)">
          {me.email && me.emailVerified
            ? `${me.email} — also works`
            : 'Email — add one above to use it as a second way in'}
        </li>
      </ul>
      {me.isPlatformAdmin && (
        <p className="mt-3 rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs">
          This account is a platform administrator. It can read and change
          every organisation on DrBell, and everything it does is logged.
        </p>
      )}
    </section>
  );
}

/**
 * Where this account is signed in, and on which phones — RX-AUTH-05.
 *
 * The point of the screen is the distinction in its own note: signing a
 * session out stops somebody *reading* the record; revoking a device also
 * stops reminders *reaching* it. Somebody who has lost a phone needs both,
 * and would otherwise leave their mother's medication times appearing on a
 * lock screen belonging to whoever found it.
 */
function Sessions({ api }: { api: ProfileTransport }) {
  const [data, setData] = useState<SessionsData | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.get<SessionsData>('/api/sessions'));
    } catch (e) {
      setError(message(e));
    }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  if (error) {
    return (
      <section className="mt-6 border-t border-(--line) pt-5">
        <h3 className="font-medium">Where you are signed in</h3>
        <p role="alert" className="mt-2 text-sm text-red-500">{error}</p>
      </section>
    );
  }
  if (!data) return null;

  const others = data.sessions.filter((s) => !s.current).length;
  const live = data.devices.filter((d) => !d.revoked);

  return (
    <section className="mt-6 border-t border-(--line) pt-5">
      <h3 className="font-medium">Where you are signed in</h3>

      {/*
        Sorted, and timestamped to the minute.
        
        Unsorted and dated only to the day, a dozen sessions from the same
        afternoon are an indistinguishable wall — and this screen exists so
        somebody can spot the one they do not recognise. Current first,
        because "is this me?" is the question being asked.
      */}
      <ul className="mt-2 space-y-1 text-sm">
        {[...data.sessions]
          .sort((a, b) => Number(b.current) - Number(a.current) || b.createdAt - a.createdAt)
          .map((s) => (
            <li key={s.id} className="flex flex-wrap items-baseline gap-2">
              <span>{describeAgent(s.userAgent)}</span>
              {s.current && (
                <span className="rounded-full bg-(--accent-soft) px-2 py-0.5 text-xs text-(--accent)">
                  this one
                </span>
              )}
              <span className="text-xs text-(--muted)">
                since{' '}
                {new Date(s.createdAt).toLocaleString(undefined, {
                  day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
                })}
              </span>
            </li>
          ))}
      </ul>

      {live.length > 0 && (
        <>
          <p className="mt-3 text-xs text-(--muted)">Phones receiving reminders</p>
          <ul className="mt-1 space-y-1 text-sm">
            {live.map((d) => (
              <li key={d.id} className="flex flex-wrap items-baseline gap-2">
                <span>{[d.manufacturer, d.model].filter(Boolean).join(' ') || d.platform}</span>
                {/* Said out loud. A phone that cannot show a notification is
                    the single most useful fact when somebody reports never
                    having been reminded. */}
                {!d.notificationsGranted && (
                  <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs text-amber-700">
                    notifications off
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      <p className="mt-3 text-xs text-(--muted)">{data.note}</p>

      {(others > 0 || live.length > 0) && (
        <button
          disabled={busy}
          onClick={async () => {
            setBusy(true);
            try {
              await api.post('/api/sessions/revoke', { scope: 'others', revokeDevices: true });
              toast.success(
                'Signed out everywhere else',
                'Reminders stop reaching those phones too.',
              );
              await load();
            } catch (e) {
              toast.error('Could not sign the others out', message(e));
            } finally {
              setBusy(false);
            }
          }}
          className="mt-3 min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-red-500 disabled:opacity-40"
        >
          {busy ? 'Signing out…' : 'Sign out everywhere else'}
        </button>
      )}
    </section>
  );
}

/** `Mozilla/5.0 (iPhone; …) Safari/…` is not a thing to show a person. */
function describeAgent(agent: string | null): string {
  if (!agent) return 'Unknown device';
  const os =
    /iPhone|iPad/.test(agent) ? 'iPhone or iPad'
    : /Android/.test(agent) ? 'Android'
    : /Mac OS X|Macintosh/.test(agent) ? 'Mac'
    : /Windows/.test(agent) ? 'Windows'
    : /Linux/.test(agent) ? 'Linux'
    : 'Unknown device';
  const browser =
    /Edg\//.test(agent) ? 'Edge'
    : /OPR\//.test(agent) ? 'Opera'
    : /Chrome\//.test(agent) ? 'Chrome'
    : /Firefox\//.test(agent) ? 'Firefox'
    : /Safari\//.test(agent) ? 'Safari'
    : null;
  return browser ? `${os} · ${browser}` : os;
}

/**
 * Taking your data with you, and deleting the account — RX-TRUST-02.
 *
 * Both are rights under the DPDP Act rather than features, and both are
 * conditions of being listed in either app store. Both endpoints had existed
 * from the beginning with nothing anywhere able to call them, which means
 * DrBell could not have been submitted and could not have answered a request
 * to erase somebody.
 */
function YourData({ me, api, onChanged }: {
  me: ProfileData;
  api: ProfileTransport;
  onChanged: () => void | Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [typed, setTyped] = useState('');
  const [error, setError] = useState<string | null>(null);

  const due = me.deletionDueAt;

  return (
    <section className="mt-6 border-t border-(--line) pt-5">
      <h3 className="font-medium">Your data</h3>

      <button
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            /*
             * Fetched and saved rather than linked.
             *
             * The export is behind a session cookie, so a plain <a download>
             * works — but only while the cookie is SameSite-compatible with
             * the navigation, and it gives no way to report a failure. This
             * way a 500 becomes a message instead of a downloaded file
             * containing the word "error".
             */
            const bundle = await api.get<unknown>('/api/me/export');
            const url = URL.createObjectURL(
              new Blob([JSON.stringify(bundle, null, 2)], { type: 'application/json' }),
            );
            const a = document.createElement('a');
            a.href = url;
            a.download = `drbell-export-${new Date().toISOString().slice(0, 10)}.json`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Your data has been downloaded');
          } catch (e) {
            toast.error('Could not download your data', message(e));
          } finally {
            setBusy(false);
          }
        }}
        className="mt-2 min-h-11 rounded-lg border border-(--line) px-4 text-sm hover:border-(--accent) disabled:opacity-40"
      >
        {busy ? 'Preparing…' : 'Download everything DrBell holds about you'}
      </button>
      <p className="mt-1 text-xs text-(--muted)">
        A JSON file: your account, the people you look after, their medicines
        and every dose recorded.
      </p>

      {due ? (
        <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 p-3 text-sm">
          <p className="font-medium text-red-600">
            This account is scheduled for deletion on{' '}
            {new Date(due).toLocaleDateString()}.
          </p>
          {/* The reassurance is the important half. Somebody who asked for
              this in a bad moment needs to know nothing has stopped yet. */}
          <p className="mt-1 text-(--muted)">
            Everything keeps working until then — reminders keep firing and
            caregivers keep being told. After that date it is erased and cannot
            be recovered.
          </p>
          <button
            onClick={async () => {
              try {
                await api.post('/api/me/delete/cancel');
                toast.success('Deletion cancelled', 'Your account is staying.');
                await onChanged();
              } catch (e) {
                toast.error('Could not cancel', message(e));
              }
            }}
            className="mt-2 min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
          >
            Keep my account
          </button>
        </div>
      ) : !confirming ? (
        <button
          onClick={() => setConfirming(true)}
          className="mt-4 min-h-11 rounded-lg border border-(--line) px-4 text-sm text-red-600 hover:border-red-500"
        >
          Delete my account
        </button>
      ) : (
        <div className="mt-4 rounded-lg border border-red-500/30 bg-red-500/5 p-3 text-sm">
          <p className="font-medium">Delete this account?</p>
          <p className="mt-1 text-(--muted)">
            Nothing happens for 30 days. Reminders keep firing and caregivers
            keep being told for the whole of that time, because stopping them
            the moment somebody taps a button would strand a person off a
            single tap. You can change your mind at any point.
          </p>
          <p className="mt-2 text-(--muted)">
            After 30 days your account, the records you own and every
            photograph are erased for good.
          </p>

          <label className="mt-3 block">
            <span className="text-xs text-(--muted)">
              Type your number, <strong className="text-(--fg)">{me.phoneNumber}</strong>, to confirm
            </span>
            <input
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              autoFocus
              className="mt-1 min-h-11 w-full rounded-lg border border-(--line) bg-(--panel) px-3"
            />
          </label>

          {error && (
            <p role="alert" className="mt-2 text-sm text-red-500">{error}</p>
          )}

          <div className="mt-3 flex flex-wrap justify-end gap-2">
            <button
              onClick={() => { setConfirming(false); setTyped(''); setError(null); }}
              className="min-h-11 rounded-lg border border-(--line) px-4 text-sm"
            >
              Keep my account
            </button>
            <button
              disabled={busy || typed.replace(/\D/g, '') !== (me.phoneNumber ?? '').replace(/\D/g, '')}
              onClick={async () => {
                setBusy(true);
                setError(null);
                try {
                  await api.post('/api/me/delete', { confirmPhone: typed });
                  toast.success('Deletion scheduled', 'You have 30 days to change your mind.');
                  setConfirming(false);
                  setTyped('');
                  await onChanged();
                } catch (e) {
                  setError(message(e));
                } finally {
                  setBusy(false);
                }
              }}
              className="min-h-11 rounded-lg bg-red-600 px-4 text-sm font-medium text-white disabled:opacity-40"
            >
              Schedule deletion
            </button>
          </div>
        </div>
      )}
    </section>
  );
}
